import React, { useEffect, useCallback, useState, useMemo, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import { graphql, useFragment } from 'react-relay';
import { mergeRefs } from 'use-callback-ref';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import Button from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger/src/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';

import type { GroupByDropdown$key } from '@atlassian/jira-relay/src/__generated__/GroupByDropdown.graphql';
import {
	GROUP_BY_DROPDOWN_EXPERIENCE,
	ANALYTICS_SOURCE,
	TEAM_NAME,
	PACKAGE_NAME,
} from '../common/constants.tsx';
import { LoadingContent } from '../common/ui/loading-content/index.tsx';
import { groupByDropdownEntryPoint } from './dropdown-content/entrypoint.tsx';
import { groupByDropdownWithSearchEntryPoint } from './dropdown-content-with-search/entrypoint.tsx';
import messages from './messages.tsx';

export type GroupByDropdownProps = {
	spreadsheetView: GroupByDropdown$key;
	onChangeGroupedByField: (fieldId: string | null | undefined) => void;
};

export const GroupByDropdown = ({
	spreadsheetView,
	onChangeGroupedByField,
}: GroupByDropdownProps) => {
	const cantGroupIssuesContentIssueTermRefreshGalaxia = expVal(
		'project-terminology-refresh',
		'isEnabled',
		false,
	)
		? messages.cantGroupIssuesContentIssueTermRefreshGalaxia
		: messages.cantGroupIssuesContentIssueTermRefresh;
	const { formatMessage } = useIntl();

	const onExperienceStart = useExperienceStart({
		experience: GROUP_BY_DROPDOWN_EXPERIENCE,
		analyticsSource: ANALYTICS_SOURCE,
	});

	const onExperienceFail = useExperienceFail({
		experience: GROUP_BY_DROPDOWN_EXPERIENCE,
	});

	useEffect(() => {
		onExperienceStart();
	}, [onExperienceStart]);

	const data = useFragment<GroupByDropdown$key>(
		graphql`
			fragment GroupByDropdown on JiraView
			@argumentDefinitions(
				isSavedListViewTechnicalChangesEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/is-saved-list-view-technical-changes-enabled.relayprovider"
				}
			) {
				id @required(action: THROW)
				... on JiraSpreadsheetView {
					jql
					filterId
					viewSettings(
						groupBy: $groupBy
						staticViewInput: $staticViewInput
						issueSearchInput: $issueSearchInput
						settings: $settings
					) {
						canEnableGrouping
						isGroupingEnabled
						groupByConfig {
							groupByField {
								fieldId
								name
								typeKey
							}
						}
					}
				}
				... on JiraIssueSearchViewMetadata {
					savedViewId @include(if: $isSavedListViewTechnicalChangesEnabled)
				}
			}
		`,
		spreadsheetView,
	);

	const groupByFieldFromQuery = useMemo(
		() =>
			data?.viewSettings?.groupByConfig?.groupByField
				? {
						fieldId: data.viewSettings.groupByConfig.groupByField.fieldId,
						name: data.viewSettings.groupByConfig.groupByField.name,
					}
				: null,
		[data?.viewSettings?.groupByConfig?.groupByField],
	);
	const prevGroupByFieldFromQuery = useRef(groupByFieldFromQuery);
	const [groupByField, setGroupByField] = useState(groupByFieldFromQuery);

	// If group by field changes in the query response, update local state to keep it in sync
	if (!isEqual(groupByFieldFromQuery, prevGroupByFieldFromQuery.current)) {
		prevGroupByFieldFromQuery.current = groupByFieldFromQuery;
		setGroupByField(groupByFieldFromQuery);
	}

	const onUpdateGroupByFieldAndRefetch = useCallback(
		(newGroupByField: { fieldId: string; name: string } | null) => {
			if (groupByField?.fieldId !== newGroupByField?.fieldId) {
				setGroupByField(newGroupByField ?? null);
				onChangeGroupedByField(newGroupByField?.fieldId);
			}
		},
		[groupByField, onChangeGroupedByField],
	);

	const issueSearchInput =
		(data?.filterId && { filterId: data.filterId }) ||
		(data?.jql && { jql: data.jql }) ||
		undefined;

	const groupByFieldId = groupByField?.fieldId;
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		groupByDropdownEntryPoint,
		{
			nodeId: data.id,
			selectedFieldId: groupByFieldId,
			issueSearchInput,
		},
	);

	const buttonTrigger = useEntryPointButtonTrigger(entryPointActions);

	addUFOCustomData({
		groupByFieldId: data?.viewSettings?.groupByConfig?.groupByField?.typeKey,
		isGrouping: data?.viewSettings?.isGroupingEnabled,
	});

	const errorFallback = useCallback(
		({ error }: { error: Error }) => {
			fireErrorAnalytics({
				meta: {
					id: 'issue-table-group-by-dropdown',
					packageName: PACKAGE_NAME,
					teamName: TEAM_NAME,
				},
				error,
				sendToPrivacyUnsafeSplunk: true,
			});

			onExperienceFail(PACKAGE_NAME, error);
			return (
				<Box xcss={errorFallbackStyles}>
					<Text>{formatMessage(messages.entrypointErrorContent)}</Text>
				</Box>
			);
		},
		[formatMessage, onExperienceFail],
	);

	if (!data?.viewSettings?.canEnableGrouping) {
		return (
			<DropdownMenu<HTMLButtonElement>
				trigger={({ triggerRef, onClick, ...triggerProps }) => (
					<Button
						{...triggerProps}
						ref={triggerRef}
						onClick={(
							event: React.MouseEvent<HTMLButtonElement>,
							analyticsEvent: UIAnalyticsEvent,
						) => {
							fireUIAnalytics(
								analyticsEvent,
								'button clicked',
								'issueTableGroupByDropdownDisabledTrigger',
							);
							onClick && onClick(event);
						}}
					>
						{formatMessage(messages.groupByLabelWithoutSelection)}
					</Button>
				)}
				placement="bottom-end"
				shouldRenderToParent
				spacing="compact"
			>
				<Box xcss={cantGroupIssuesStyles}>
					<Text>
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? cantGroupIssuesContentIssueTermRefreshGalaxia
								: messages.cantGroupIssuesContent,
						)}
					</Text>
				</Box>
			</DropdownMenu>
		);
	}

	if (fg('group_by_any_field')) {
		return (
			<PopupTrigger
				entryPoint={groupByDropdownWithSearchEntryPoint}
				entryPointParams={{
					nodeId: data.id,
					issueSearchInput,
				}}
				entryPointProps={{
					selectedFieldId: groupByFieldId,
					onUpdateGroupByFieldAndRefetch,
					savedViewId: data?.savedViewId,
					issueSearchInput,
				}}
				shouldRenderToParent
				placement="bottom-end"
				fallback={<LoadingContent />}
				errorFallback={errorFallback}
				trigger={(triggerProps) => (
					<SpotlightTarget name="group-by-any-field-dropdown">
						<Button
							{...triggerProps}
							isSelected={Boolean(groupByField) || triggerProps['aria-expanded']}
							testId="issue-table-group-by-dropdown.ui.group-by-dropdown"
							onClick={(
								_: React.MouseEvent<HTMLButtonElement>,
								analyticsEvent: UIAnalyticsEvent,
							) => {
								fireUIAnalytics(
									analyticsEvent,
									'button clicked',
									'issueTableGroupByDropdownTrigger',
									{
										isGroupByAnyFieldExperimentEnabled: true,
									},
								);
							}}
						>
							{groupByField?.name
								? formatMessage(messages.groupByLabelWithASelection, {
										groupByField: groupByField.name,
									})
								: formatMessage(messages.groupByLabelWithoutSelection)}
						</Button>
					</SpotlightTarget>
				)}
			/>
		);
	}

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, onClick, isSelected, ...triggerProps }) => (
				<Button
					{...triggerProps}
					isSelected={Boolean(groupByField) || isSelected}
					testId="issue-table-group-by-dropdown.ui.group-by-dropdown"
					ref={mergeRefs([triggerRef, buttonTrigger])}
					onClick={(
						event: React.MouseEvent<HTMLButtonElement>,
						analyticsEvent: UIAnalyticsEvent,
					) => {
						fireUIAnalytics(analyticsEvent, 'button clicked', 'issueTableGroupByDropdownTrigger');
						onClick && onClick(event);
					}}
				>
					{groupByField?.name
						? formatMessage(messages.groupByLabelWithASelection, {
								groupByField: groupByField.name,
							})
						: formatMessage(messages.groupByLabelWithoutSelection)}
				</Button>
			)}
			placement="bottom-end"
			shouldRenderToParent
			spacing="compact"
		>
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				id="async-jira-spreadsheet-group-by-dropdown"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
				errorFallback={errorFallback}
				runtimeProps={{ onUpdateGroupByFieldAndRefetch, savedViewId: data?.savedViewId }}
				fallback={<LoadingContent />}
			/>
		</DropdownMenu>
	);
};

const cantGroupIssuesStyles = xcss({
	marginInline: 'space.300',
	marginBlock: 'space.200',
	width: '250px',
	whiteSpace: 'initial',
});

const errorFallbackStyles = xcss({
	padding: 'space.200',
	width: '250px',
});
